import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { Redirect, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { RouteWhenAuthenticated } from 'common/components';

import BusinessVerificationFlow from './BusinessVerificationFlow';
import BusinessVerificationInformationSubmitted from './BusinessVerificationInformationSubmitted';
import BusinessVerificationIntro from './BusinessVerificationIntro';
import BusinessVerificationSuccess from './BusinessVerificationSuccess';
import { KybProcessContext } from './KybProcessContext';
import { SectionConfigurationContext } from './SectionConfigurationContext';
import { KybSectionMapping, SectionConfiguration } from './types';
import { fetchKybProcessConfiguration, fetchLatestKybProcess } from '../../../apis/legitimizer/kyb/api';
import { KybProcessConfiguration } from '../../../apis/legitimizer/types';
import PageLoadError from '../../../core/PageLoadError';

export const Root = styled.main`
  display: flex;
  flex-flow: column nowrap;
  flex-shrink: 0;
  align-items: center;
  min-height: 100%;
  width: 100%;
  background-color: ${cssThemedValue('surfaceMain')};

  & > * + * {
    margin-block-start: 32px;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block-start: 50px;
`;

const KYB_SECTIONS_MAPPING: KybSectionMapping = {
  basicCompanyInfo: 'basic-info',
  beneficiaries: 'beneficiaries',
  bankAccounts: 'bank-details',
  documents: 'documents',
};

const QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: false,
};

const BusinessVerificationRoutes = () => {
  const { path, url: kybRootUrl, params } = useRouteMatch<{ slug: string }>();

  const { pathname } = useLocation();

  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    rootRef.current?.scrollIntoView();
  }, [pathname]);

  const {
    data: latestKybProcess = null,
    isLoading: isKybProcessLoading,
    error: kybProcessError,
  } = useQuery(
    ['/v1/kyb/merchants/processes/latest', params.slug],
    () => fetchLatestKybProcess(params.slug),
    QUERY_OPTIONS,
  );

  const {
    data: kybConfiguration = null,
    isLoading: isKybConfigurationLoading,
    error: kybConfigurationError,
  } = useQuery(
    ['/kyb/processes/configuration', latestKybProcess?.id],
    () => (latestKybProcess?.id ? fetchKybProcessConfiguration(latestKybProcess.id) : null),
    QUERY_OPTIONS,
  );

  if (kybConfigurationError || kybProcessError) {
    return <PageLoadError />;
  }

  if (isKybProcessLoading || isKybConfigurationLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  if (!latestKybProcess || !kybConfiguration || latestKybProcess.state !== 'OPEN') {
    return <Redirect to="/" />;
  }

  const sectionConfigurationMapper = (config: KybProcessConfiguration) => {
    const enabledSections = config.sections.map(section => KYB_SECTIONS_MAPPING[section.key]);
    return config.sections.reduce(
      (config: SectionConfiguration, section) => ({
        ...config,
        [KYB_SECTIONS_MAPPING[section.key]]: section.fieldSets.reduce(
          (sections, fieldSet) => ({
            ...sections,
            [fieldSet.key]: fieldSet.fields.map(field => field),
          }),
          {},
        ),
      }),
      { enabledSections },
    );
  };

  return (
    <KybProcessContext.Provider
      value={{
        countryCode: latestKybProcess.countryCode,
        merchantId: latestKybProcess.merchantId,
        processId: latestKybProcess.id,
        isImportedMerchant: latestKybProcess.merchantCreationSource === 'unknown',
      }}
    >
      <SectionConfigurationContext.Provider value={sectionConfigurationMapper(kybConfiguration)}>
        <Root ref={rootRef}>
          <Switch>
            <RouteWhenAuthenticated exact path={`${path}/flow/:section`} component={BusinessVerificationFlow} />
            <RouteWhenAuthenticated exact path={`${path}/intro`} component={BusinessVerificationIntro} />
            <RouteWhenAuthenticated
              exact
              path={`${path}/information-submitted`}
              component={BusinessVerificationInformationSubmitted}
            />
            <RouteWhenAuthenticated exact path={`${path}/success`} component={BusinessVerificationSuccess} />
            <RouteWhenAuthenticated component={() => <Redirect to={`${kybRootUrl}/intro`} />} />
          </Switch>
        </Root>
      </SectionConfigurationContext.Provider>
    </KybProcessContext.Provider>
  );
};

export default BusinessVerificationRoutes;
